import { useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import type { Property } from '@vuokramani/shared';
import { useMe } from '../../lib/auth.ts';
import { useOnboardingStatus } from '../../lib/finance.ts';
import { useUpdateMe } from '../../lib/properties.ts';
import s from './onboarding.module.css';

/**
 * Aloitusohje uudelle vuokranantajalle: neljä askelta, joista tehdyt merkitään. Näkyy
 * yhteenvedossa, kunnes kaikki on tehty tai käyttäjä piilottaa sen. Sulkeminen tallentuu
 * käyttäjälle, joten ohje ei palaa, vaikka tiedot myöhemmin muuttuisivat.
 */
export function Onboarding({ properties }: { properties: Property[] }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const closed = me?.onboardingClosed ?? true;
  const { data: status } = useOnboardingStatus(!closed && properties.length > 0);
  const updateMe = useUpdateMe();
  const first = properties[0];
  const base = first ? `/vuokranantaja/asunnot/${first.id}` : null;

  const steps = [
    {
      key: 'property',
      done: properties.length > 0,
      to: '/vuokranantaja/asunnot/uusi',
    },
    {
      key: 'lease',
      done: properties.some((p) => p.currentLease !== null),
      to: base ? `${base}/vuokrasuhde/uusi` : null,
    },
    {
      key: 'invoices',
      done: Boolean(status?.hasInvoiceTemplate) || Boolean(me?.iban),
      to: base ? `${base}/talous` : null,
    },
    {
      key: 'expenses',
      done: Boolean(status?.hasExpense),
      to: base ? `${base}/talous?osa=kulut` : null,
    },
  ];
  const doneCount = steps.filter((step) => step.done).length;
  const next = steps.find((step) => !step.done);

  const allDone = Boolean(status) && !next;
  const close = updateMe.mutate;

  // Kaikki tehty: suljetaan pysyvästi.
  useEffect(() => {
    if (!closed && allDone) close({ onboardingClosed: true });
  }, [closed, allDone, close]);

  // Ensimmäinen kerta (ei asuntoja) näytetään aina; muuten kunnes suljettu.
  if (properties.length > 0 && (closed || !status || !next)) return null;

  function hide() {
    close({ onboardingClosed: true });
  }

  return (
    <section className={s.box} aria-labelledby="onboarding-title">
      <div className={s.head}>
        <div>
          <h2 id="onboarding-title" className={s.title}>
            {t('onboarding.title')}
          </h2>
          <p className={s.progress}>
            {t('onboarding.progress', { done: doneCount, total: steps.length })}
          </p>
        </div>
        {properties.length > 0 && (
          <button type="button" className={s.hide} onClick={hide} aria-label={t('onboarding.hide')}>
            <X size={16} aria-hidden="true" />
            <span>{t('onboarding.hide')}</span>
          </button>
        )}
      </div>
      <ol className={s.steps}>
        {steps.map((step, i) => {
          const isNext = step === next;
          return (
            <li key={step.key} className={`${s.step} ${step.done ? s.done : ''}`}>
              <span className={s.marker} aria-hidden="true">
                {step.done ? <Check size={14} strokeWidth={3} /> : i + 1}
              </span>
              <span className={s.text}>
                <span className={s.stepTitle}>
                  {t(`onboarding.${step.key}.title`)}
                  {step.done && <span className="visually-hidden"> ({t('onboarding.done')})</span>}
                </span>
                {!step.done && (
                  <span className={s.stepText}>{t(`onboarding.${step.key}.text`)}</span>
                )}
              </span>
              {!step.done && step.to && (
                <Link to={step.to} className={isNext ? s.actionPrimary : s.action}>
                  {t(`onboarding.${step.key}.action`)}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
